import Drawer, { DrawerProps } from '@liquidweb-dev/undercurrent/Drawer';
import DrawerContent from '@liquidweb-dev/undercurrent/DrawerContent';
import DrawerHeader from '@liquidweb-dev/undercurrent/DrawerHeader';
import HighlightBlock from '@liquidweb-dev/undercurrent/HighlightBlock';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import DisplayTypeIcon from 'components/standard/DisplayTypeIcon';
import hostingDetailsAlsowith from 'containers/pages/servers/details/hostingDetailsAlsowith';
import useAssetDetails from 'modules/queries/asset/useDetails';
import { isServerTypeWithHelpDocs } from 'utility/constants/serverTypeWithHelpDoc';
import CloudBareMetalServerDetails from './CloudBareMetalServerDetails';
import CloudMetalServerDetails from './CloudMetalServerDetails';
import CloudVPSServerDetails from './CloudVPSServerDetails';

interface DrawerDocsDetailsProps extends DrawerProps {
	onClose: () => void;
	uniqId: string;
}

const DrawerDocsDetails = ({
	uniqId,
	onClose,
	...drawerProps
}: DrawerDocsDetailsProps) => {
	const { data: assetDetailsData } = useAssetDetails({
		uniq_id: uniqId,
		alsowith: hostingDetailsAlsowith,
	});

	const displayType = assetDetailsData?.displayType;
	const serverType = assetDetailsData?.type?.toLocaleLowerCase() || '';
	const displayLabel =
		displayType === 'GPU Accelerated' ? 'GPU Server' : displayType;

	const HelpDocsContent = ({ type }: { type: string }) => {
		if (!isServerTypeWithHelpDocs(type)) {
			return null;
		}

		switch (type) {
			case 'cloud.vps':
			case 'cloud.vps.win':
			case 'pkg-cloud.vps':
			case 'pkg-cloud.vps.win':
				return <CloudVPSServerDetails />;
			case 'cloud.metal':
			case 'cloud.metal.win':
			case 'pkg-cloud.metal':
			case 'pkg-cloud.metal.win':
				return <CloudMetalServerDetails />;
			case 'bare.metal':
			case 'bare.metal.win':
			case 'pkg-bare.metal':
			case 'pkg-bare.metal.win':
				return <CloudBareMetalServerDetails />;
			// TODO: DEV-5510: Integrate the GPU Accelerated Server details once the title, content and icon are available
			default:
				return null;
		}
	};

	return (
		<Drawer anchor="right" onClose={onClose} {...drawerProps}>
			<DrawerHeader onClose={onClose}>Documentation</DrawerHeader>
			<DrawerContent sx={{ gap: 450 }}>
				<Stack gap={200}>
					<Typography variant="title-md">Get the most out of your</Typography>
					<Stack direction="row" justifyContent="start">
						<HighlightBlock
							sx={(theme) => ({
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								borderRadius: theme.borderRadius.roundedSm,
								border: `1px solid ${theme.vars.palette.uc.highlight.border.weak}`,
								background: 'rgba(236, 235, 255, 0.50)',
								paddingY: 50,
								paddingX: 100,
								gap: 100,
							})}
						>
							<DisplayTypeIcon
								displayType={displayType}
								sx={({ vars, size }) => ({
									color: vars.palette.uc.highlight.icon.main,
									width: size.sm,
									height: size.sm,
								})}
							/>
							<Typography variant="title-md">{displayLabel}</Typography>
						</HighlightBlock>
					</Stack>
				</Stack>
				<Stack gap={200}>
					<Typography variant="label-xl" display="flex">
						Help Docs:
					</Typography>
					<Stack>{HelpDocsContent({ type: serverType })}</Stack>
				</Stack>
			</DrawerContent>
		</Drawer>
	);
};

export default DrawerDocsDetails;
